import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SafeSelect as Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/SafeSelect';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ModuleSubscriptionDialog from './ModuleSubscriptionDialog';

// Icons
import { 
  Crown,
  Users,
  GraduationCap,
  HardDrive,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  CreditCard,
  Calendar,
  Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManager = ({ user }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [tenantModules, setTenantModules] = useState([]);
  const [optionalModules, setOptionalModules] = useState([]);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const [subResponse, usageResponse, modulesResponse, allModulesResponse, optionalModulesResponse] = await Promise.all([
        axios.get(`${API}/tenant/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tenant/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tenant/modules`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tenant/modules/available-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tenant/modules/optional-active`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSubscription(subResponse.data);
      setUsage(usageResponse.data);
      
      // Récupérer les modules de base depuis la configuration
      // Ne plus dépendre de enabled_modules qui peut être vide
      const allModules = allModulesResponse.data || [];
      
      // Afficher uniquement les modules de base configurés
      const baseModules = allModules.filter(module => 
        module.is_base_module === true && module.is_required === true
      );
      
      setTenantModules(baseModules);
      
      // Récupérer les modules optionnels actifs
      setOptionalModules(optionalModulesResponse.data || []);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Erreur lors du chargement des données d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleSubscriptionSuccess = () => {
    // Rafraîchir les données après une souscription réussie
    fetchSubscriptionData();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'TRIAL': return 'bg-blue-500';
      case 'ACTIVE': return 'bg-green-500';
      case 'EXPIRED': return 'bg-red-500';
      case 'CANCELLED': return 'bg-red-600';
      case 'NO_SUBSCRIPTION': return 'bg-gray-500';
      case 'SUSPENDED': return 'bg-orange-600';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'TRIAL': return 'Période d\'essai';
      case 'ACTIVE': return 'Actif';
      case 'EXPIRED': return 'Expiré';
      case 'CANCELLED': return 'Annulé';
      case 'NO_SUBSCRIPTION': return 'Aucun abonnement';
      case 'SUSPENDED': return 'Suspendu';
      default: return status || 'En attente';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Chargement des données d'abonnement...</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun abonnement trouvé
          </h3>
          <p className="text-gray-600">
            Contactez l'administrateur pour configurer votre abonnement.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statut */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion de l'Abonnement</h1>
          <p className="text-gray-600 mt-1">
            Gérez votre plan et surveillez votre utilisation
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Badge className={`${getStatusColor(subscription.subscription.status)} text-white`}>
            {getStatusText(subscription.subscription.status)}
          </Badge>
          
          {user.role === 'ADMIN' && (
            <>
              <Button onClick={() => setModuleDialogOpen(true)}>
                <Package className="w-4 h-4 mr-2" />
                Souscrire aux Modules
              </Button>
              
              <ModuleSubscriptionDialog
                open={moduleDialogOpen}
                onOpenChange={setModuleDialogOpen}
                tenantModules={tenantModules}
                optionalModules={optionalModules}
                onSuccess={handleModuleSubscriptionSuccess}
              />
            </>
          )}
        </div>
      </div>

      {/* Pack de Base Souscrit */}
      <Card className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Package className="w-5 h-5 mr-2 text-orange-600" />
              Pack de Base Souscrit
            </div>
            <Badge className={`${getStatusColor(subscription.subscription.status)} text-white`}>
              {getStatusText(subscription.subscription.status)}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tenantModules && tenantModules.length > 0 ? (
            <div className="space-y-4">
              {/* Affichage du Pack de Base */}
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Pack de Base</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Modules essentiels pour la gestion complète de votre établissement
                    </p>
                    
                    {/* Liste des modules inclus */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <p className="text-xs font-semibold text-gray-700 uppercase mb-3">Modules inclus :</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {tenantModules.map((module) => (
                          <div key={module.module_id} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{module.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Prix total du pack */}
              <div className="bg-orange-600 text-white rounded-lg p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm uppercase tracking-wide opacity-90">Prix mensuel du pack</p>
                    <p className="text-xs opacity-75 mt-1">Facturé chaque mois</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">
                      {formatPrice(tenantModules.reduce((sum, m) => sum + m.monthly_price, 0))}
                      <span className="text-lg ml-2">GNF</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">ℹ️ Information :</span> Ce pack regroupe tous les modules de base nécessaires au fonctionnement de votre établissement. Le renouvellement est automatique chaque mois.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>Aucun module souscrit pour le moment</p>
              <p className="text-sm mt-1">Procédez au paiement du Pack de Base pour activer votre établissement</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modules Optionnels Actifs */}
      {optionalModules.length > 0 && (
        <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                Modules Optionnels Actifs
              </div>
              <Badge className="bg-blue-600 text-white">
                {optionalModules.length} module{optionalModules.length > 1 ? 's' : ''}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {optionalModules.map((module) => (
                <div key={module.module_id} className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 mb-1">{module.name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{module.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          Activé le {new Date(module.activated_at).toLocaleDateString('fr-FR')}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {module.payment_method === 'CASH' ? 'Cash' : module.payment_method === 'CHEQUE' ? 'Chèque' : 'Orange Money'}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-bold text-blue-600">
                        {formatPrice(module.monthly_price)} GNF
                      </div>
                      <p className="text-xs text-gray-500 mt-1">par mois</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Prix total des modules optionnels */}
              <div className="bg-blue-600 text-white rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm uppercase tracking-wide opacity-90">Coût mensuel des modules optionnels</p>
                    <p className="text-xs opacity-75 mt-1">Facturé chaque mois</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      {formatPrice(optionalModules.reduce((sum, m) => sum + m.monthly_price, 0))}
                      <span className="text-base ml-2">GNF</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Utilisation */}
      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Étudiants</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {usage.current_usage.students} / {usage.limits.max_students}
              </div>
              <Progress 
                value={usage.percentage_used.students} 
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-2">
                {usage.percentage_used.students}% utilisé
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Enseignants</CardTitle>
              <GraduationCap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {usage.current_usage.teachers} / {usage.limits.max_teachers}
              </div>
              <Progress 
                value={usage.percentage_used.teachers} 
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-2">
                {usage.percentage_used.teachers}% utilisé
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Stockage</CardTitle>
              <HardDrive className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Math.round(usage.current_usage.storage_mb / 1024 * 100) / 100} GB
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Sur {Math.round(usage.limits.max_storage_mb / 1024)} GB disponibles
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fonctionnalités */}
      <Card>
        <CardHeader>
          <CardTitle>Fonctionnalités Incluses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subscription.limits.features && Object.entries(subscription.limits.features).map(([feature, enabled]) => (
              <div key={feature} className="flex items-center">
                {enabled ? (
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-400 mr-2" />
                )}
                <span className={enabled ? 'text-gray-900' : 'text-gray-400'}>
                  {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManager;